/**
 * Utilitaires pour le calcul et l'affichage des scores de performance
 */

/**
 * Retourne la couleur (classe CSS ou nom de variante) en fonction du score (0-1)
 * @param {number} score - Le score entre 0 et 1
 * @returns {string} - 'success', 'warning', 'error' ou 'gray'
 */
export function getScoreColor(score) {
    if (score === null || score === undefined) return 'gray';
    if (score >= 0.9) return 'success';
    if (score >= 0.5) return 'warning';
    return 'error';
}

/**
 * Retourne la classe CSS d'arrière-plan pour une ligne de tableau en fonction du score
 * @param {number} score - Le score entre 0 et 1
 * @returns {string} - Classe CSS Tailwind (ex: 'bg-green-50')
 */
export function getRowBg(score) {
    if (score === null || score === undefined) return '';
    if (score >= 0.9) return 'bg-green-50';
    if (score >= 0.5) return 'bg-yellow-50';
    return 'bg-red-50';
}

/**
 * Détermine le statut d'une métrique Web Vitals
 * @param {string|number} value - La valeur de la métrique
 * @param {string} metric - Le nom de la métrique ('lcp', 'fid', 'cls', 'tbt')
 * @returns {string} - 'good', 'needs-improvement', 'poor' ou 'unknown'
 */
export function getMetricStatus(value, metric) {
    if (!value || value === '-') return 'unknown';

    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.]/g, '')) : value;
    if (isNaN(numValue)) return 'unknown';

    switch (metric) {
        case 'lcp':
            return numValue <= 2500 ? 'good' : numValue <= 4000 ? 'needs-improvement' : 'poor';
        case 'fid':
            return numValue <= 100 ? 'good' : numValue <= 300 ? 'needs-improvement' : 'poor';
        case 'cls':
            return numValue <= 0.1 ? 'good' : numValue <= 0.25 ? 'needs-improvement' : 'poor';
        case 'tbt':
            return numValue <= 200 ? 'good' : numValue <= 600 ? 'needs-improvement' : 'poor';
        default:
            return 'unknown';
    }
}

/**
 * Retourne la couleur associée au statut d'une métrique
 * @param {string} status - Le statut ('good', 'needs-improvement', 'poor')
 * @returns {string} - 'success', 'warning', 'error' ou 'gray'
 */
export function getMetricColor(status) {
    switch (status) {
        case 'good': return 'success';
        case 'needs-improvement': return 'warning';
        case 'poor': return 'error';
        default: return 'gray';
    }
}

/**
 * Retourne la couleur (classe CSS ou nom de variante) en fonction du score SEO (0-100)
 * @param {number} score - Le score entre 0 et 100
 * @returns {string} - 'success', 'warning', 'error' ou 'gray'
 */
export function getSeoScoreColor(score) {
    if (score === null || score === undefined) return 'gray';
    if (score >= 90) return 'success';
    if (score > 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 50) return 'warning';
    return 'error';
}

/**
 * Retourne la classe CSS d'arrière-plan pour une ligne de tableau en fonction du score SEO
 * @param {number} score - Le score entre 0 et 100
 * @returns {string} - Classe CSS Tailwind
 */
export function getSeoRowBg(score) {
    if (score === null || score === undefined) return '';
    if (score >= 90) return 'bg-green-50';
    if (score > 80) return 'bg-emerald-50';
    if (score >= 60) return 'bg-amber-50';
    if (score >= 50) return 'bg-orange-100';
    if (score >= 40) return 'bg-orange-50';
    return 'bg-red-50';
}
